import {Component, OnDestroy, OnInit} from '@angular/core';
import {OrdineEsolverGroup} from '../../_models/OrdineEsolverGroup';
import {Subscription} from 'rxjs';
import {GroupColumn} from '../../_models/OrdiniGroupColumns';
import {LocalService} from '../../_services/local.service';
import {ModalController} from '@ionic/angular';
import {Ordine, OrdineCreate} from '../../_models/Ordine';
import {OrdineEsolver} from '../../_models/OrdineEsolver';
import {StatoEnum} from '../../_models/generated/stato-enum';
import {SaveMessage} from '../../_helpers/save-message';
import {EventiService} from '../../_services/eventi.service';
import {OrganizzazioniService} from '../../_services/organizzazioni.service';
import {OrdineGroup} from '../../_models/OrdineGroup';
import {Organizzazione} from '../../_models/Organizzazione';
import {TipoRisorsa} from '../../_models/generated/tipo-risorsa'
import {RisorseService} from '../../_services/risorse.service';
import {LavorazioniService} from '../../_services/lavorazioni.service';
import {Risorsa} from '../../_models/Risorsa';
import {Lavorazione} from '../../_models/Lavorazione';
import {codiceValido, codiciRisorseProduzione, richiedeSceltaPersonale} from '../../_helpers/scelta-personale';

@Component({
  selector: 'app-ordine-esolver-group-launch',
  templateUrl: './ordine-esolver-group-launch.component.html',
  styleUrls: ['./ordine-esolver-group-launch.component.scss'],
  standalone: false
})
export class OrdineEsolverGroupLaunchComponent implements OnInit, OnDestroy {
  public ordineEsolverGroup: OrdineEsolverGroup;
  ordineGroup: OrdineGroup;
  subscriptions: Subscription;
  groupColumns: GroupColumn[];
  changeRisorsa = true;
  changeLavorazione = true;
  loading = false;
  // Feature 152: se un ordine ha la pianificata non personale, il valore di gruppo è obbligatorio.
  bloccoRisorsa = false;
  bloccoLavorazione = false;
  private codiciRisorse: string[] = [];
  private codiciLavorazioni: string[] = [];
  private bloccoSubscriptions = new Subscription();

  constructor(
    private saveMessage: SaveMessage,
    private localService: LocalService,
    private eventiService: EventiService,
    private organizzazioniService: OrganizzazioniService,
    private risorseService: RisorseService,
    private lavorazioniService: LavorazioniService,
    private modalCtrl: ModalController) { }

  ngOnInit() {
    this.groupColumns = this.organizzazioniService.groupColumns;
    this.subscriptions = this.organizzazioniService.currentOrganizzazione
      .subscribe((_org: Organizzazione) => {
        this.groupColumns = this.organizzazioniService.groupColumns;
      });
    this.bloccoSubscriptions.add(this.risorseService.myRisorse
      .subscribe((risorse: Risorsa[]) => this.aggiornaBloccoRisorsa(risorse)));
    this.bloccoSubscriptions.add(this.lavorazioniService.myLavorazioni
      .subscribe((lavorazioni: Lavorazione[]) => this.aggiornaBloccoLavorazione(lavorazioni)));
  }

  aggiornaBloccoRisorsa(risorse: Risorsa[]) {
    this.codiciRisorse = codiciRisorseProduzione(risorse);
    const filtro = this.organizzazioniService.risorsePersonal;
    this.bloccoRisorsa = this.pianificate((o: OrdineEsolver) => o.risorsa)
      .some((codice: string) => richiedeSceltaPersonale(codice, this.codiciRisorse, filtro));
    if (!this.bloccoRisorsa) {
      return;
    }
    this.changeRisorsa = true;
    if (!codiceValido(this.ordineEsolverGroup.risorsa, this.codiciRisorse, filtro)) {
      this.ordineEsolverGroup.risorsa = '';
    }
  }

  aggiornaBloccoLavorazione(lavorazioni: Lavorazione[]) {
    this.codiciLavorazioni = (lavorazioni ?? []).map((lavorazione: Lavorazione) => lavorazione.codice);
    const filtro = this.organizzazioniService.lavorazioniPersonal;
    this.bloccoLavorazione = this.pianificate((o: OrdineEsolver) => o.lavorazione)
      .some((codice: string) => richiedeSceltaPersonale(codice, this.codiciLavorazioni, filtro));
    if (!this.bloccoLavorazione) {
      return;
    }
    this.changeLavorazione = true;
    if (!codiceValido(this.ordineEsolverGroup.lavorazione, this.codiciLavorazioni, filtro)) {
      this.ordineEsolverGroup.lavorazione = '';
    }
  }

  get risorsaNonValida(): boolean {
    return this.bloccoRisorsa && !codiceValido(this.ordineEsolverGroup.risorsa, this.codiciRisorse, true);
  }

  get lavorazioneNonValida(): boolean {
    return this.bloccoLavorazione && !codiceValido(this.ordineEsolverGroup.lavorazione, this.codiciLavorazioni, true);
  }

  get lancioBloccato(): boolean {
    return this.risorsaNonValida || this.lavorazioneNonValida;
  }

  private pianificate = (campo: (ordine: OrdineEsolver) => string): string[] =>
    (this.ordineEsolverGroup?.ordini ?? []).map(campo);

  async launchOrdineEsolverGroup() {
    if (!this.ordineEsolverGroup?.ordini?.length) {
      this.saveMessage.messageError("Nessun ordine da lanciare.");
      return;
    }

    this.loading = true;
    this.ordineGroup = {
      ...this.localService.getEmptyOrder(),
      ordini: []
    };
    const promiseArray: Promise<Ordine | void>[] = [];
    this.ordineEsolverGroup.ordini.forEach((_ord: OrdineEsolver) => {
      const newOrdine: OrdineCreate = {
        rifOrdine: _ord.rifOrdine,
        idDocumento: _ord.idDocumento,
        idRiga: _ord.idRiga,
        idRigaLavorazione: _ord.idRigaLavorazione,
        organizzazioneId: this.organizzazioniService.organizzazioneId,
        risorsaId: 0,
        lavorazioneId: 0,
        dataRegistrazione: _ord.dataRegistrazione,
        numRegistrazione: _ord.numRegistrazione,
        codiceArticolo: _ord.codiceArticolo,
        descArticolo: _ord.descArt,
        varianteArt: _ord.varianteArt,
        quantita: _ord.quantita,
        lotto: 1,
        quantitaOrdinataUdM2: _ord.quantitaOrdinataUdM2,
        quantitaLavorata: 0,
        quantitaScartata: 0,
        quantitaNonConforme: 0,
        tempoLav: _ord.tempo,
        codMagPrincipale: _ord.codMagPrincipale,
        codAreaMagPrinc: _ord.codAreaMagPrinc,
        omacState: '',
        stato: StatoEnum.LANCIATO,
        flgTerminato: false,
        statoElimina: 0,
        dataTermine: _ord.dataTermine,
        codiceRisorsa: this.changeRisorsa ? this.ordineEsolverGroup.risorsa : _ord.risorsa,
        codiceLavorazione: this.changeLavorazione ? this.ordineEsolverGroup.lavorazione : _ord.lavorazione,
        commessa: _ord.commessa,
        umTempo: _ord.umTempo,
        note: '',
        notaInterna: _ord.notaInterna,
        notaFase: _ord.notaFase,
        rifLottoProdotto: _ord.rifLottoAlfanum,
        udM: _ord.udM,
        udM2: _ord.udM2,
        numFase: _ord.numFase,
        rifOdP: _ord.rifOdP,
        //codCliente: _ord.id,
        ragSocialeCliente: _ord.ragSocialeCliente,
        faseContoLavoro: _ord.faseContoLavoro,
        codTerzista: _ord.codTerzista,
        ragSocialeTerzista: _ord.ragSocialeTerzista,
        lottoCode: this.organizzazioniService.autoLottoCode
      };
      promiseArray.push(this.createOrdine(newOrdine));
    });
    await Promise.all(promiseArray)
      .then(() => this.modalCtrl.dismiss(this.ordineGroup, 'launch'))
      .finally(() => this.loading = false);
  }

  createOrdine(ordine: OrdineCreate): Promise<Ordine | void> {
    return this.localService.createOrdine(ordine)
      .then((ordineLocal: Ordine) => {
        this.ordineGroup.ordini.push(ordineLocal);
        this.saveMessage.messageInfo('Ordine lanciato con successo.');
        this.eventiService.creaEvento(StatoEnum.LANCIATO, ordineLocal, new Date())
          .catch(() => this.saveMessage.messageError("Problemi durante la creazione dell'evento di lancio."));
        return ordineLocal;
      })
      .catch(() => {
        this.saveMessage.messageError("Problemi durante il lancio dell'ordine.");
      });
  }

  checkGroupColumn = (columnName: string) => this.groupColumns
    .some(column => column.columnName === columnName && column.active);

  dismiss = () => this.modalCtrl.dismiss(true, 'save');

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.bloccoSubscriptions.unsubscribe();
  }

  protected readonly TipoRisorsa = TipoRisorsa
}
